import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import './checkoutDisplay.css';

interface CheckoutModalProps {
  open: boolean;
  title: string;
  closeLabel: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Centred over the page, a full-height panel from the end side as the cart is, or from the start side as a phone menu is. */
  placement?: 'center' | 'drawer' | 'menu' | 'popup';
  /** A visible heading, drawn in a row with the close button so neither ever covers the content. */
  heading?: React.ReactNode;
}

// Everything a visitor can reach with the Tab key inside the dialog.
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Moves focus into the dialog when it opens and hands it back to whatever opened it on close.
export function useDialogFocus(open: boolean, panel: React.RefObject<HTMLDivElement>): void {
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    // Without preventScroll a dialog taller than the viewport opens already scrolled past its own first field.
    panel.current?.focus({ preventScroll: true });
    return () => opener?.focus?.();
  }, [open, panel]);
}

// Freezes the page behind the dialog, so scrolling the form never scrolls the product page away.
export function useScrollLock(open: boolean): void {
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);
}

// Wraps Tab around the dialog's own controls so the keyboard never lands on the frozen page behind.
export function trapTab(event: React.KeyboardEvent<HTMLDivElement>, panel: HTMLDivElement | null): void {
  const items = panel ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)) : [];
  if (items.length === 0) return;
  const edge = event.shiftKey ? items[0] : items[items.length - 1];
  if (document.activeElement !== edge && document.activeElement !== panel) return;
  event.preventDefault();
  (event.shiftKey ? items[items.length - 1] : items[0]).focus();
}

// Shows the cart or the COD form over the page: Escape and the backdrop dismiss it, and its answers outlive a dismissal.
export const CheckoutModal: React.FC<CheckoutModalProps> = ({ open, title, closeLabel, onClose, children, placement = 'center', heading }) => {
  const panel = useRef<HTMLDivElement>(null);
  const pressedBackdrop = useRef(false);
  useDialogFocus(open, panel);
  useScrollLock(open);

  // Escape dismisses; Tab stays inside. The form itself keeps its answers, so neither exit loses them.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') return onClose();
    if (event.key === 'Tab') trapTab(event, panel.current);
  };

  const close = (
    <button type="button" className="checkout-modal-close" aria-label={closeLabel} onClick={onClose}>
      <X size={18} />
    </button>
  );
  return (
    <div
      className="checkout-modal-overlay"
      data-placement={placement}
      hidden={!open}
      onKeyDown={handleKeyDown}
      onMouseDown={(e) => { pressedBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (pressedBackdrop.current && e.target === e.currentTarget) onClose(); }}
    >
      <div className="checkout-modal-panel" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={panel}>
        {heading ? <div className="checkout-modal-head">{heading}{close}</div> : close}
        <div className="checkout-modal-body">{children}</div>
      </div>
    </div>
  );
};
